import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FIELD_ERROR } from './field-styles';
import { uniqueId } from './ids';

/**
 * Casella di spunta con etichetta proiettata: può contenere collegamenti, come quelli ai
 * termini d'uso e all'informativa privacy, che si aprono senza cambiare la spunta.
 */
@Component({
  selector: 'bs-checkbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxComponent), multi: true },
  ],
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <div class="flex items-start gap-3">
      <input
        #control
        type="checkbox"
        [id]="id"
        class="mt-0.5 size-5 shrink-0 cursor-pointer rounded-md border-stone-300 accent-ink disabled:cursor-not-allowed dark:accent-bsite-400"
        [checked]="checked()"
        [disabled]="disabled()"
        [required]="required()"
        [attr.aria-invalid]="error() ? 'true' : null"
        [attr.aria-describedby]="error() ? errorId : null"
        [attr.data-testid]="testId()"
        (change)="onToggle($event)"
        (blur)="onTouched()"
      />
      <label [for]="id" class="cursor-pointer text-sm text-stone-600 dark:text-stone-300">
        <ng-content />
      </label>
    </div>
    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class CheckboxComponent implements ControlValueAccessor {
  readonly error = input<string | null>(null);
  readonly required = input(false);
  readonly testId = input<string | null>(null);

  protected readonly id = uniqueId('bs-checkbox');
  protected readonly errorId = `${this.id}-error`;
  protected readonly errorClass = FIELD_ERROR;
  protected readonly checked = signal(false);
  protected readonly disabled = signal(false);
  private readonly control = viewChild.required<ElementRef<HTMLInputElement>>('control');

  private onChange: (value: boolean) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.checked.set(value === true);
  }
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  focus(): void {
    this.control().nativeElement.focus();
  }

  protected onToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checked.set(checked);
    this.onChange(checked);
  }
}
